/**
 * The two touch points the shared HTTP transport has with activity tracking (MD-0099).
 *
 * Kept dependency-free so `api/client.ts` and `api/uploads.ts` can import it without a
 * cycle through the auth store. Both are no-ops until a tracker registers.
 */

export const CLIENT_ACTIVITY_HEADER = 'X-Client-Activity';

interface ActivityClientHooks {
  headerValue: () => string | null;
  noteAction: () => void;
}

let hooks: ActivityClientHooks | null = null;

export function registerActivityClientHooks(next: ActivityClientHooks | null): void {
  hooks = next;
}

/** Snapshot once per logical request; reuse across automatic network attempts. */
export function getClientActivityHeader(): string | null {
  try {
    const header = hooks?.headerValue();
    return header ? `${header}; rid=${crypto.randomUUID()}` : null;
  } catch {
    // Telemetry never breaks a request.
    return null;
  }
}

/** A write request left this tab: the screen's first action, if none happened yet. */
export function noteActivityAction(): void {
  try {
    hooks?.noteAction();
  } catch {
    // Telemetry never breaks a request.
  }
}

/** Kept equal to backend READ_ONLY_POSTS by test_action_names.py. */
const READ_ONLY_POST_ROUTES = [
  '/api/web-admin/camera-presence/zones/{camera_id}/preview',
  '/api/web-admin/cameras/archive/viewers/{viewer}',
  '/api/web-admin/stock/recipes/preview',
  '/api/web-admin/stock/receipts/packaging-preview',
  '/api/web-admin/stock/inventories/{inventory_id}/metadata-preview',
  '/api/web-admin/bank-statements/preview',
  '/api/web-admin/documents/relations-summary',
  '/api/web-admin/payments/duplicate-hints',
  '/api/web-admin/invoices/{invoice_id}/duplicate-check',
  '/api/web-admin/stock/products/costs/batch',
  '/api/web-admin/stock/products/totals',
  '/api/web-admin/stock/receipts/match-products',
] as const;

/** POST queries must not end the screen's time before the first work action. */
export function isActivityReadOnlyPost(url: string): boolean {
  let path: string;
  try {
    path = new URL(url, 'http://localhost').pathname;
  } catch {
    return false;
  }
  const parts = path.split('/');
  return READ_ONLY_POST_ROUTES.some((route) => {
    const pattern = route.split('/');
    return (
      pattern.length === parts.length &&
      pattern.every((part, index) =>
        part.startsWith('{') ? Boolean(parts[index]) : part === parts[index],
      )
    );
  });
}
